import { OptionType } from '@/enums/OptionType';
import {
  OrderPaymentStatus,
  OrderPaymentType,
  OrderStatus,
  OrderType,
} from '@/enums/OrderEnums';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { CajaPaymentMethod, ICaja } from '@/interfaces/ICaja';
import { ICartItem } from '@/interfaces/ICart';
import { IItem } from '@/interfaces/IItem';
import { IOption } from '@/interfaces/IOption';
import { IOrder } from '@/interfaces/IOrder';
import { IOrderItem } from '@/interfaces/IOrderItem';
import { IOrderItemOption } from '@/interfaces/IOrderItemOption';
import { round2 } from '@/utils';

export function paymentMethodToCajaMethodOrNull(
  paymentMethod: number | PaymentMethod | null | undefined
): CajaPaymentMethod | null {
  switch (paymentMethod) {
    case PaymentMethod.Cash:
      return 'cash';
    case PaymentMethod.Card:
      return 'card';
    case PaymentMethod.Online:
    case PaymentMethod.RedSys:
    case PaymentMethod.CyberSource:
    case PaymentMethod.MetroBank:
    case PaymentMethod.Stripe:
      return 'online';
    case PaymentMethod.BankTransfer:
      return 'banktransfer';
    case PaymentMethod.QR:
      return 'qr';
    case PaymentMethod.Invitation:
      return 'invitation';
    case PaymentMethod.Employee:
      return 'employee';
    case PaymentMethod.CustomerAccount:
      return 'customer';
    case PaymentMethod.Club:
      return 'club';
    default:
      return null;
  }
}

export function paymentMethodToCajaMethod(
  paymentMethod: PaymentMethod
): CajaPaymentMethod {
  const bucket = paymentMethodToCajaMethodOrNull(paymentMethod);
  if (!bucket) {
    throw new Error(`Invalid payment method (${paymentMethod})`);
  }
  return bucket;
}

export function isOnlineLikePaymentMethod(
  paymentMethod: number | PaymentMethod | null | undefined
): boolean {
  return paymentMethodToCajaMethodOrNull(paymentMethod) === 'online';
}

export function isPaidOnlineLikeOrder(
  order: Pick<IOrder, 'paymentMethod' | 'paymentStatus' | 'paymentType'>
): boolean {
  const isOnlineLike =
    order.paymentType === OrderPaymentType.Online ||
    isOnlineLikePaymentMethod(order.paymentMethod);

  return isOnlineLike && order.paymentStatus === OrderPaymentStatus.Paid;
}

export function getPaidOrderCancelDeclineGuardError(params: {
  order: Pick<IOrder, 'totalPaid'>;
  status: OrderStatus;
}): string | null {
  const totalPaid = Number(params.order.totalPaid ?? 0);
  if (!(totalPaid > 0)) {
    return null;
  }

  if (params.status === OrderStatus.Declined) {
    return 'No puede rechazar un pedido pagado; reembolse primero el pago';
  }

  if (params.status === OrderStatus.Canceled) {
    return 'No puede cancelar un pedido pagado; reembolse primero el pago';
  }

  return null;
}

export function getOnlineAcceptCajaGuardError(params: {
  order: Pick<
    IOrder,
    | 'status'
    | 'paymentMethod'
    | 'paymentStatus'
    | 'paymentType'
    | 'totalPaid'
    | 'total'
    | 'paymentsByType'
    | 'cajaId'
  >;
  caja:
    | Pick<ICaja, 'status' | 'posDeviceId' | 'enabled' | 'currentStatusId'>
    | null
    | undefined;
  effectiveTier: number;
  branchSettingUseCajas: boolean | null | undefined;
}): string | null {
  const totalPaid = Number(params.order.totalPaid ?? 0);
  const total = Number(params.order.total ?? 0);
  const allocatableAmount = round2(Math.max(totalPaid, total));

  // 'club' rides the online rail: app club-balance payments settle caja-less
  // and get allocated into the selected caja on accept, just like online.
  const orderCajaBucket = paymentMethodToCajaMethodOrNull(
    params.order.paymentMethod
  );
  const isPaidOnlineBucketOrder =
    params.order.paymentStatus === OrderPaymentStatus.Paid &&
    (orderCajaBucket === 'online' || orderCajaBucket === 'club');

  // Partially-paid orders (checkout balance debit + in-person remainder)
  // keep the customer-chosen bucket but still carry caja-less club money.
  // Order-level heuristic (club bucket recorded + not yet in a caja); the
  // server's per-payment EXISTS check is the authority — edge cases surface
  // through its error toasts.
  const hasUnallocatedClubMoney =
    parseOrderPaymentsByType(params.order).club > 0 &&
    params.order.cajaId == null;

  const requiresCajaForOnlineAccept =
    params.order.status === OrderStatus.Received &&
    (isPaidOnlineBucketOrder || hasUnallocatedClubMoney) &&
    allocatableAmount > 0 &&
    params.effectiveTier > 0 &&
    !!params.branchSettingUseCajas;

  if (!requiresCajaForOnlineAccept) {
    return null;
  }

  const caja = params.caja;

  if (!caja) {
    return 'Seleccione una caja';
  }

  if (caja.enabled === false) {
    return 'La caja está deshabilitada; seleccione otra';
  }

  if (caja.status === 'closed' || caja.currentStatusId == null) {
    return 'Abra la caja para aceptar pedidos con pago online';
  }

  if (caja.posDeviceId != null) {
    return 'Esta caja es de un POS; seleccione una caja de tienda';
  }

  return null;
}

interface ParsedPayments {
  cash: number;
  card: number;
  online: number;
  qr: number;
  invitation: number;
  employee: number;
  customer: number;
  banktransfer: number;
  club: number;
}

export function parseOrderPaymentsByType(
  order: Pick<IOrder, 'paymentsByType'>
): ParsedPayments {
  const defaultPayments: ParsedPayments = {
    cash: 0,
    card: 0,
    online: 0,
    qr: 0,
    invitation: 0,
    employee: 0,
    customer: 0,
    banktransfer: 0,
    club: 0,
  };

  if (!order.paymentsByType) {
    return defaultPayments;
  }

  // POSITIONAL: mirrors the API's parseOrderPaymentsByType. Append-only,
  // never reorder.
  const paymentTypes: (keyof ParsedPayments)[] = [
    'cash',
    'card',
    'online',
    'qr',
    'invitation',
    'employee',
    'customer',
    'banktransfer',
    'club',
  ];

  return order.paymentsByType.reduce(
    (acc, payment, index) => {
      if (index < paymentTypes.length) {
        acc[paymentTypes[index]] = payment || 0;
      }
      return acc;
    },
    { ...defaultPayments }
  );
}

/**
 * Payment methods whose money lands in the card/online/qr buckets of
 * `paymentsByType` — the buckets that require AFIP auto-invoicing.
 * Reuses the canonical method-to-bucket mapping so this can never
 * diverge from how the backend aggregates `paymentsByType`.
 */
export function isAutoInvoicePaymentMethod(method: PaymentMethod): boolean {
  const bucket = paymentMethodToCajaMethodOrNull(method);
  return bucket === 'card' || bucket === 'online' || bucket === 'qr';
}

/** True when the order has recorded payments in the card/online/qr buckets. */
export function orderHasAutoInvoicePayments(order: IOrder): boolean {
  const payments = parseOrderPaymentsByType(order);
  return payments.card + payments.online + payments.qr > 0;
}

/**
 * Invoiceable total once non-sale settlements (invitación/empleado) are
 * excluded — comped money is not fiscal revenue. Clamped at 0. Legacy orders
 * with `paymentsByType = null` parse to zeros, so their total is unchanged.
 */
export function orderInvoiceableTotal(order: IOrder): number {
  const payments = parseOrderPaymentsByType(order);
  const comps = round2(payments.invitation + payments.employee);
  return round2(Math.max(0, round2(Number(order.total ?? 0) - comps)));
}

/** True when the order has a total but nothing invoiceable (100% comped). */
export function isOrderFullyComped(order: IOrder): boolean {
  const total = Number(order.total ?? 0);
  return total > 0 && orderInvoiceableTotal(order) <= 0;
}

/**
 * Invoice eligibility gate: something must remain to invoice after the
 * comps exclusion. Also treats `total <= 0` orders as not invoiceable.
 */
export function isOrderInvoiceable(order: IOrder): boolean {
  return orderInvoiceableTotal(order) > 0;
}

export function setPaymentByType(
  order: IOrder,
  paymentMethod: PaymentMethod,
  amount: number
) {
  // POSITIONAL: append-only, never reorder (see parseOrderPaymentsByType).
  const paymentTypes = [
    'cash',
    'card',
    'online',
    'qr',
    'invitation',
    'employee',
    'customer',
    'banktransfer',
    'club',
  ];
  const paymentType = paymentMethodToCajaMethod(paymentMethod);
  const index = paymentTypes.indexOf(paymentType);

  if (index === -1) {
    throw new Error(`Invalid payment type: ${paymentMethod}`);
  }

  let newPaymentsByType: number[];

  if (!order.paymentsByType) {
    newPaymentsByType = new Array(paymentTypes.length).fill(0);
  } else {
    newPaymentsByType = [...order.paymentsByType];
    while (newPaymentsByType.length < paymentTypes.length) {
      newPaymentsByType.push(0);
    }
  }

  newPaymentsByType[index] = round2(amount);

  order.paymentsByType = newPaymentsByType;
}

function isOnlineLikeOrder(
  order: Pick<IOrder, 'paymentMethod' | 'paymentType'>
): boolean {
  return (
    order.paymentType === OrderPaymentType.Online ||
    isOnlineLikePaymentMethod(order.paymentMethod)
  );
}

// Note: For online-like orders, an order should only be considered "new"
// (thus visible in the PoS new orders list) once the payment is fully Paid.
// This mirrors the backend behavior where online orders are only surfaced after approval.
// Exception: any collected money (totalPaid > 0, e.g. a checkout club-balance
// debit whose MercadoPago remainder was abandoned) makes the order visible —
// it holds customer funds, so staff must be able to see and resolve it.
// Note: This could cause an online order to disappear if it's reimbursed.
export function isNewOrder(order: IOrder) {
  const onlineLike = isOnlineLikeOrder(order);
  return (
    order.status === OrderStatus.Received &&
    (!onlineLike ||
      order.paymentStatus === OrderPaymentStatus.Paid ||
      Number(order.totalPaid ?? 0) > 0)
  );
}

// Note: Pending orders should exclude online-unpaid orders, aligning with the server's
// computed `pending` column and pending orders filter. Only Paid (or non-online) should be pending.
// Note: This could cause an online order to disappear if it's reimbursed
export function isPendingOrder(order: IOrder) {
  if (
    order.archived ||
    order.status === OrderStatus.Declined ||
    order.status === OrderStatus.Canceled ||
    order.status === OrderStatus.Received
  )
    return false;

  if (
    order.type !== OrderType.Seat &&
    order.status === OrderStatus.Finished &&
    order.paymentStatus === OrderPaymentStatus.Paid
  )
    return false;

  const onlineLike = isOnlineLikeOrder(order);
  if (onlineLike && order.paymentStatus !== OrderPaymentStatus.Paid) {
    return false;
  }

  return true;
}

export function orderItemOptionsToText({
  orderItem,
}: {
  orderItem: IOrderItem;
}) {
  const mainStringParts: string[] = [];
  const stringParts: string[] = [];

  // Group options by optionItem using a Map
  const optionsMap = new Map<IOption, IOrderItemOption[]>();

  for (const option of orderItem.options ?? []) {
    const optionItem = option.optionItem;

    if (!optionItem) {
      throw new Error('Option item is missing');
    }

    if (!optionItem.option) {
      throw new Error('Option is missing');
    }

    if (!optionsMap.has(optionItem.option)) {
      optionsMap.set(optionItem.option, []);
    }

    optionsMap.get(optionItem.option)!.push(option);
  }

  let lastOptionId = -1;
  for (const [option, optionItems] of optionsMap) {
    const parts =
      option.main || option.mandatory || option.min
        ? mainStringParts
        : stringParts;

    for (const optionItem of optionItems) {
      const prefix =
        lastOptionId === option.id
          ? ''
          : `<span class="tw-font-medium tw-text-gray-400">${option.title}</span> `;

      if (option.multiple || option.type === OptionType.Add) {
        parts.push(
          `${prefix}${optionItem.val}x ${optionItem.optionItem?.title}`
        );
      } else {
        parts.push(`${prefix}${optionItem.optionItem?.title}`);
      }

      lastOptionId = option.id;
    }
  }

  return mainStringParts.concat(stringParts).join(', ');
}

// Shows a list of items in the order (2x Pizza, 1x Coke), with optional maxItems, in which case it will show "2x Pizza, 1x Coke and 3 more items"
export function orderItemsToText({
  order,
  maxItems,
}: {
  order: IOrder;
  maxItems?: number;
}) {
  if (!order.orderItems) {
    return '';
  }

  const items = order.orderItems
    .filter((item) => !item.canceledAt)
    .map((item) => {
      const parts = [`${item.amount}x ${item.item?.name}`];

      return parts.join(' ');
    });

  if (maxItems && items.length > maxItems) {
    return `${items.slice(0, maxItems).join(', ')} y ${
      items.length - maxItems
    } más`;
  }

  return items.join(', ');
}

export function itemToItemOptions(item: IItem) {
  const itemOptions: IOption[] = [];

  if (item.options) {
    itemOptions.push(...item.options);
  }

  if (item.itemOptions) {
    for (const iO of item.itemOptions) {
      if (!iO.option) {
        throw new Error('Item option is missing');
      }

      itemOptions.push({
        ...iO.option,
        mandatory: iO.mandatory ?? iO.option.mandatory,
        multiple: iO.multiple ?? iO.option.multiple,
        min: iO.min ?? iO.option.min,
        max: iO.max ?? iO.option.max,
        active: iO.active ?? iO.option.active,
      });
    }
  }

  return itemOptions;
}

export function calculateItemPrice({
  item,
  optionValues,
  applyTax,
}: {
  item: IItem;
  optionValues: Record<number, Record<number, number>>;
  applyTax: (price: number) => number;
}) {
  const itemOptions: IOption[] = itemToItemOptions(item);

  const basePrice = item.discountedPrice ?? item.price;

  let itemPrice = basePrice;

  let optionsPrice = 0,
    variantPrice: null | number = null;

  for (const key in optionValues) {
    const optionId = parseInt(key);
    const itemOption = itemOptions.find((o) => o.id === optionId);

    const value = optionValues[optionId];

    if (!itemOption) {
      console.log('Item option is missing', {
        key,
        optionId,
        oV: optionValues[key],
        itemOptions,
      });

      if (Object.values(value).some((v) => v > 0)) {
        throw new Error('Item option is missing');
      } else {
        continue;
      }
    }

    for (const valueKey in value) {
      const optionItemId = parseInt(valueKey);
      const optionItemValue = value[optionItemId];

      if (optionItemValue === 0) {
        continue;
      }

      const optionItem = itemOption?.optionItems?.find(
        (oi) => oi.id === optionItemId
      );

      if (!optionItem) {
        throw new Error('Option item is missing');
      }

      const optionItemPrice = optionItem.discountedCost ?? optionItem.cost;
      const optionItemTotalPrice = optionItemPrice * optionItemValue;

      if (itemOption.main) {
        variantPrice = optionItemTotalPrice;
      } else {
        optionsPrice += optionItemTotalPrice;
      }
    }
  }

  /* console.log({
    itemPrice,
    variantPrice,
    optionsPrice,
  }); */

  if (variantPrice !== null) {
    itemPrice = variantPrice;
  }

  itemPrice += optionsPrice;

  return applyTax(itemPrice);
}

export function optionValuesToText({
  item,
  optionValues,
}: {
  item: IItem;
  optionValues: Record<number, Record<number, number>>;
}) {
  const stringParts: string[] = [];

  const itemOptions: IOption[] = itemToItemOptions(item);

  for (const iO of itemOptions) {
    const values = optionValues[iO.id!];

    if (!values) {
      continue;
    }

    const valuesSum = Object.values(values).reduce((a, b) => a + b, 0);

    if (!valuesSum) {
      continue;
    }

    for (const key in values) {
      const value = values[key];

      if (!value) {
        continue;
      }

      const optionItem = iO.optionItems?.find((oi) => oi.id === parseInt(key));

      if (!optionItem) {
        //throw new Error('Option item is missing');
        stringParts.push('[opción no encontrada]');
      } else if (iO.multiple || iO.type === OptionType.Add) {
        stringParts.push(`${value}x ${optionItem.title}`);
      } else {
        stringParts.push(optionItem.title);
      }
    }
  }

  return stringParts.join(', ');
}

/**
 * Transforms an OrderItem back into a CartItem.
 * @param orderItem The OrderItem to be transformed.
 * @returns The resulting CartItem.
 */
export function orderItemToCartItem(orderItem: IOrderItem): ICartItem {
  const cartItem: ICartItem = {
    itemId: orderItem.itemId,
    item: orderItem.item,
    index: orderItem.index,
    quantity: orderItem.amount,
    userComment: orderItem.comment,
    optionValues: {},
    unitPrice: orderItem.price,
    totalPrice: orderItem.total,
  };

  for (const option of orderItem.options) {
    const optionId = option.optionItem.option.id;
    const optionItemId = option.optionItem.id;
    const val = option.val;

    if (!cartItem.optionValues[optionId]) {
      cartItem.optionValues[optionId] = {};
    }

    cartItem.optionValues[optionId][optionItemId] = val;
  }

  return cartItem;
}

export function orderMatchesSearch(order: IOrder, search: string) {
  /*
  if (this.parsedSearchText.trim()) {
          const search = this.parsedSearchText.trim().toLowerCase();

          let contains = false;

          if (order.userName) {
            contains = order.userName.toLowerCase().includes(search);
          }

          if (order.comment) {
            contains = contains || order.comment.toLowerCase().includes(search);
          }

          if (order.userAddressComments) {
            contains =
              contains ||
              order.userAddressComments.toLowerCase().includes(search);
          }

          if (order.userAddress) {
            contains =
              contains || order.userAddress.toLowerCase().includes(search);
          }

          if (order.userAddress2) {
            contains =
              contains || order.userAddress2.toLowerCase().includes(search);
          }

          if (!contains) continue;
        }
          */

  if (!search) return true;

  search = search.trim().toLowerCase();

  if (!search) return true;

  if (order.userName && order.userName.toLowerCase().includes(search)) {
    return true;
  }

  if (order.comment && order.comment.toLowerCase().includes(search)) {
    return true;
  }

  if (order.userTel && order.userTel.toLowerCase().includes(search)) {
    return true;
  }

  if (order.humanId && (order.humanId + '').toLowerCase().includes(search)) {
    return true;
  }

  if (
    order.userAddressComments &&
    order.userAddressComments.toLowerCase().includes(search)
  ) {
    return true;
  }

  if (order.userAddress && order.userAddress.toLowerCase().includes(search)) {
    return true;
  }

  if (order.userAddress2 && order.userAddress2.toLowerCase().includes(search)) {
    return true;
  }

  return false;
}
